import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, Loader2, Send, ArrowDownLeft, ArrowUpRight, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("kcis_admin_token");
  return { Authorization: `Bearer ${t}` };
}

const STATUS_PILL = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  protocol_generated: "bg-purple-100 text-purple-800",
};

function CreateReferralForm({ onCreated }) {
  const [clientId, setClientId] = useState("");
  const [assignedEmail, setAssignedEmail] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId || !assignedEmail) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/kuhas/referrals`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          client_id: clientId,
          assigned_practitioner_email: assignedEmail,
          note: note || null,
        }),
      });
      const body = await r.json();
      if (!r.ok) {
        const detail = body?.detail || body;
        if (detail?.code === "referral_initiation_not_permitted") {
          toast.error(
            "Your credential tier does not include referral initiation. Only Level II Associate Trichologists can create referrals."
          );
        } else {
          toast.error(detail?.message || detail?.code || `HTTP ${r.status}`);
        }
        return;
      }
      toast.success("Referral sent");
      setClientId(""); setAssignedEmail(""); setNote("");
      onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}
          className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3"
          data-testid="create-referral-form">
      <div className="font-display text-lg text-slate-900">Refer a client</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-xs">
          <span className="text-slate-600">Client ID</span>
          <input value={clientId} onChange={(e) => setClientId(e.target.value)} required
                 className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm font-mono"
                 data-testid="referral-client-id" />
        </label>
        <label className="block text-xs">
          <span className="text-slate-600">Receiving practitioner email</span>
          <input type="email" value={assignedEmail} onChange={(e) => setAssignedEmail(e.target.value)} required
                 className="mt-1 w-full rounded-lg border border-slate-200 px-3 h-9 text-sm"
                 placeholder="practitioner@example.com"
                 data-testid="referral-assigned-email" />
        </label>
      </div>
      <label className="block text-xs">
        <span className="text-slate-600">Note (optional)</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      </label>
      <button type="submit" disabled={saving}
              className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium h-9 px-4 inline-flex items-center gap-1.5"
              data-testid="referral-send-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Send referral
      </button>
      <p className="text-xs text-slate-500">
        Server-enforced: only accounts with an active Level II Associate Trichologist enrollment (or completion)
        may create referrals. Level I / III / DTS / HHFW / Herbal accounts will be blocked with 403.
      </p>
    </form>
  );
}

function ReferralCard({ r, direction, onAccepted }) {
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/kuhas/referrals/${r.id}/accept`, {
        method: "POST", headers: authHeaders(),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail?.message || `HTTP ${res.status}`);
      toast.success("Referral accepted");
      onAccepted();
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5"
              data-testid={`referral-${direction}-${r.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {direction === "incoming"
              ? <ArrowDownLeft className="w-4 h-4 text-emerald-800" />
              : <ArrowUpRight className="w-4 h-4 text-purple-900" />}
            <span className="font-mono text-sm text-slate-900">
              client {r.client_id}
            </span>
            <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${STATUS_PILL[r.status] || "bg-slate-100"}`}>
              {r.status.replace(/_/g, " ")}
            </span>
          </div>
          <div className="mt-2 text-xs text-slate-700">
            {direction === "incoming"
              ? <>from <b className="font-mono">{r.referring_practitioner_id}</b></>
              : <>to <b className="font-mono">{r.assigned_practitioner_id}</b></>}
          </div>
          {r.note && <div className="mt-2 text-xs text-slate-600 italic bg-slate-50 rounded-lg p-2">{r.note}</div>}
          <div className="mt-2 text-xs text-slate-400">
            {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
          </div>
        </div>
        {direction === "incoming" && r.status === "pending" && (
          <button onClick={accept} disabled={busy}
                  className="rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs h-8 px-3 inline-flex items-center gap-1"
                  data-testid={`referral-accept-${r.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Accept
          </button>
        )}
      </div>
    </article>
  );
}

export default function PractitionerReferrals() {
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("incoming");

  const load = async () => {
    setLoading(true);
    try {
      const [i, o] = await Promise.all([
        fetch(`${API}/api/kuhas/referrals/incoming`, { headers: authHeaders() }).then((r) => r.json()),
        fetch(`${API}/api/kuhas/referrals/outgoing`, { headers: authHeaders() }).then((r) => r.json()),
      ]);
      setIncoming(i.items || []);
      setOutgoing(o.items || []);
    } catch (e) { toast.error(String(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const active = tab === "incoming" ? incoming : outgoing;

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/practitioner" className="text-xs text-purple-900 hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Practitioner dashboard
            </Link>
            <span className="font-display text-lg text-slate-900">Referrals</span>
          </div>
          <button onClick={load} disabled={loading}
                  className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1">
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="practitioner-referrals">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Referrals</h1>
          <p className="mt-1 text-sm text-slate-600">
            Level II Associate Trichologists screen and refer. Level III, HHFW, and DTS practitioners
            receive and accept referrals into their protocol authorship pipeline.
          </p>
        </div>

        <CreateReferralForm onCreated={load} />

        <div className="flex items-center gap-2 text-xs">
          <button onClick={() => setTab("incoming")}
                  className={`px-3 h-8 rounded-full inline-flex items-center gap-1 ${
                    tab === "incoming" ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
                  }`}
                  data-testid="tab-incoming">
            <ArrowDownLeft className="w-3 h-3" /> Incoming ({incoming.length})
          </button>
          <button onClick={() => setTab("outgoing")}
                  className={`px-3 h-8 rounded-full inline-flex items-center gap-1 ${
                    tab === "outgoing" ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
                  }`}
                  data-testid="tab-outgoing">
            <ArrowUpRight className="w-3 h-3" /> Outgoing ({outgoing.length})
          </button>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : active.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
              No {tab} referrals.
            </div>
          ) : (
            active.map((r) => (
              <ReferralCard key={r.id} r={r} direction={tab} onAccepted={load} />
            ))
          )}
        </div>
      </main>
    </div>
  );
}
